import React, { useRef, useEffect } from 'react';
import {
  DndContext, PointerSensor, TouchSensor, KeyboardSensor,
  useSensor, useSensors, sortableKeyboardCoordinates
} from '../KanbanDndContext';
import { AppHeader } from './AppHeader';
import { AppSubHeaderAndSidebars } from './AppSubHeaderAndSidebars';
import AppViewRouter from '../AppViewRouter';
import { GlobalModalsContainer } from '../modals/GlobalModalsContainer';
import { AppDragOverlayPortal } from './AppDragOverlayPortal';
import { UndoToast } from '../UndoToast';

export interface AppDesktopLayoutProps {
  customCollisionDetection: any;
  handleDragStart: (event: any) => void;
  handleDragEnd: (event: any) => void;
  handleDragOver: (event: any) => void;
  handleDragCancel: (event?: any) => void;
  isDarkMode: boolean;
  modalRegistry: any;
  viewTabsState: any;
  handleSearchPrev: () => void;
  handleSearchNext: () => void;
  navigateToSearchResult: (index: number) => void;
  lastNavigatedIndexRef: React.MutableRefObject<number>;
  isFilterActive: boolean;
  showUndo: boolean;
  setShowUndo: (show: boolean) => void;
  notifications: any[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  handleNavigateToNotificationTask: (projectId: string, taskId: string | number) => void;
  viewMode: any;
  isProjectView: boolean;
  isConceptView: boolean;
  isStandardTaskView: boolean;
  isProjectEditor: boolean;
  dropdownProjects: any[];
  globalAllAssignees: string[];
  globalAvailableTypes: string[];
  globalAvailableStatuses: string[];
  showToolsDropdown: boolean;
  setShowToolsDropdown: (show: boolean) => void;
  zoomToFit: () => void;
  isFitMode: boolean;
  setAiAssistantTab: (tab: any) => void;
  workspaceProjects: any[];
  showFilters: boolean;
  isRightSidebarOpen: boolean;
  isSidebarCollapsed: boolean;
  sidebarPeople: any[];
  sidebarPeopleData: any;
  activeDragId: any;
  droppedZoneId: any;
  filterAssignee: string;
  peopleWithActiveVorgang: Set<string>;
  handleToggleSidebarPersonFilter: (name: string) => void;
  handleOpenSidebarContactCard: (name: string) => void;
  handleClearAssigneeFilter: () => void;
  handleAddPersonToProject: (personData?: any) => void;
  deferredVisibleTasks: any[];
  deferredTasks: any[];
  handleAddTask: any;
  addIdea: any;
  ganttState: any;
  todayStr: string;
  containerHeight: number;
  registerViewDragHandlers: (handlers: any) => void;
  handleReorderTask: any;
  handleEditTaskMemo: any;
  handleEditTaskGlobalMemo: any;
  currentUserId?: string;
  userEmail?: string;
  otherWorkspaceProjects: any[];
  handleAddToHistory: any;
  timelineRef: any;
  isTimeMachineOpen: boolean;
  setIsTimeMachineOpen: (open: boolean) => void;
  categories: any[];
  setIsRightSidebarOpen: (open: boolean) => void;
  commDragMeta: any;
  activeDragTask: any;
  initialActiveNodeRectRef: React.MutableRefObject<any>;
  editingTask: any;
}

export const AppDesktopLayout: React.FC<AppDesktopLayoutProps> = (props) => {
  const {
    customCollisionDetection,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragCancel,
    isDarkMode,
    modalRegistry,
    viewTabsState,
    handleSearchPrev,
    handleSearchNext,
    navigateToSearchResult,
    lastNavigatedIndexRef,
    isFilterActive,
    showUndo,
    setShowUndo,
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    handleNavigateToNotificationTask,
    viewMode,
    isProjectView,
    isConceptView,
    isStandardTaskView,
    isProjectEditor,
    dropdownProjects,
    globalAllAssignees,
    globalAvailableTypes,
    globalAvailableStatuses,
    showToolsDropdown,
    setShowToolsDropdown,
    zoomToFit,
    isFitMode,
    setAiAssistantTab,
    workspaceProjects,
    showFilters,
    isRightSidebarOpen,
    isSidebarCollapsed,
    sidebarPeople,
    sidebarPeopleData,
    activeDragId,
    droppedZoneId,
    filterAssignee,
    peopleWithActiveVorgang,
    handleToggleSidebarPersonFilter,
    handleOpenSidebarContactCard,
    handleClearAssigneeFilter,
    handleAddPersonToProject,
    deferredVisibleTasks,
    deferredTasks,
    handleAddTask,
    addIdea,
    ganttState,
    todayStr,
    containerHeight,
    registerViewDragHandlers,
    handleReorderTask,
    handleEditTaskMemo,
    handleEditTaskGlobalMemo,
    currentUserId,
    userEmail,
    otherWorkspaceProjects,
    handleAddToHistory,
    timelineRef,
    isTimeMachineOpen,
    setIsTimeMachineOpen,
    categories,
    setIsRightSidebarOpen,
    commDragMeta,
    activeDragTask,
    initialActiveNodeRectRef,
    editingTask,
  } = props;

  const toolsDropdownRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (showToolsDropdown && toolsDropdownRef.current && !toolsDropdownRef.current.contains(target)) {
        setShowToolsDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showToolsDropdown, setShowToolsDropdown]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragCancel={handleDragCancel}
      autoScroll={{
        canScroll: (element) => element !== document.scrollingElement && element !== document.body
      }}
    >
      <div className={`flex flex-col h-screen bg-[var(--bg-app)] transition-colors duration-300 font-sans selection:bg-indigo-100 dark:selection:bg-indigo-900/40 ${isDarkMode ? 'dark' : ''} print:!h-auto`}>
        {/* 1. Global Header (Layer 1) - Fixed at 64px */}
        <AppHeader
          {...modalRegistry}
          {...viewTabsState}
          handleSearchPrev={handleSearchPrev}
          handleSearchNext={handleSearchNext}
          navigateToSearchResult={navigateToSearchResult}
          lastNavigatedIndexRef={lastNavigatedIndexRef}
          isFilterActive={isFilterActive}
          showUndo={showUndo}
          setShowUndo={setShowUndo}
          notifications={notifications}
          unreadCount={unreadCount}
          markAsRead={markAsRead}
          markAllAsRead={markAllAsRead}
          handleNavigateToNotificationTask={handleNavigateToNotificationTask}
        />

        <AppSubHeaderAndSidebars
          viewMode={viewMode}
          isProjectView={isProjectView}
          isConceptView={isConceptView}
          isStandardTaskView={isStandardTaskView}
          isProjectEditor={isProjectEditor}
          viewTabsState={viewTabsState}
          dropdownProjects={dropdownProjects}
          globalAllAssignees={globalAllAssignees}
          globalAvailableTypes={globalAvailableTypes}
          globalAvailableStatuses={globalAvailableStatuses}
          toolsDropdownRef={toolsDropdownRef}
          isFilterActive={isFilterActive}
          zoomToFit={zoomToFit}
          isFitMode={isFitMode}
          setAiAssistantTab={setAiAssistantTab}
          workspaceProjects={workspaceProjects}
          showFilters={showFilters}
          isRightSidebarOpen={isRightSidebarOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          sidebarPeople={sidebarPeople}
          sidebarPeopleData={sidebarPeopleData}
          activeDragId={activeDragId}
          droppedZoneId={droppedZoneId}
          filterAssignee={filterAssignee}
          peopleWithActiveVorgang={peopleWithActiveVorgang}
          handleToggleSidebarPersonFilter={handleToggleSidebarPersonFilter}
          handleOpenSidebarContactCard={handleOpenSidebarContactCard}
          handleClearAssigneeFilter={handleClearAssigneeFilter}
          handleAddPersonToProject={handleAddPersonToProject}
        />

        {/* Main View Router Container */}
        <div className="flex flex-1 pt-[64px] overflow-hidden print:!pt-0 print:!h-auto" inert={modalRegistry.isBlockingDialogOpen || Boolean(editingTask) || isTimeMachineOpen}>
          <AppViewRouter
            {...viewTabsState}
            isProjectView={isProjectView}
            isConceptView={isConceptView}
            isProjectEditor={isProjectEditor}
            workspaceProjects={workspaceProjects}
            deferredVisibleTasks={deferredVisibleTasks}
            deferredTasks={deferredTasks}
            handleAddTask={handleAddTask}
            addIdea={addIdea}
            ganttState={ganttState}
            todayStr={todayStr}
            containerHeight={containerHeight}
            droppedZoneId={droppedZoneId}
            activeDragId={activeDragId}
            registerViewDragHandlers={registerViewDragHandlers}
            handleReorderTask={handleReorderTask}
            handleEditTaskMemo={handleEditTaskMemo}
            handleEditTaskGlobalMemo={handleEditTaskGlobalMemo}
            currentUserId={currentUserId}
            userEmail={userEmail}
            otherWorkspaceProjects={otherWorkspaceProjects}
            dropdownProjects={dropdownProjects}
            globalAllAssignees={globalAllAssignees}
            handleAddToHistory={handleAddToHistory}
            timelineRef={timelineRef}
          />
        </div>

        <GlobalModalsContainer
          modalRegistry={modalRegistry}
          isTimeMachineOpen={isTimeMachineOpen}
          setIsTimeMachineOpen={setIsTimeMachineOpen}
          isProjectEditor={isProjectEditor}
          categories={categories}
          isRightSidebarOpen={isRightSidebarOpen}
          setIsRightSidebarOpen={setIsRightSidebarOpen}
          handleAddTask={handleAddTask}
        />

        <AppDragOverlayPortal
          commDragMeta={commDragMeta}
          activeDragTask={activeDragTask}
          initialActiveNodeRectRef={initialActiveNodeRectRef}
        />

        <UndoToast />
      </div>
    </DndContext>
  );
};
